import React, { useCallback, useEffect, useMemo, useState, memo } from 'react';
import { fromJS, Map } from 'immutable';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { reloadEvents } from 'src/actions/company/companyEvents';
import connectOptions, { mergeProps } from '../../utils/connectOptions';
import {
  revertUpdate,
  findBuyers,
  findProjects,
  handleUpdateCompany,
  findApprovalLists,
  addBuyer,
  closeValidationError,
  updateLastResearcher,
} from '../../actions/company/companyTarget';
import Popups from '../../components/Popup';
import { unwrap, getChanged } from '../../utils/ChangeSpy';
import BaseAutoCompleteContainer from '../AutoComplete';
import PrettyError from '../../components/decorators/PrettyError';
import { PrettyErrorProvider } from '../../contexts/PrettyErrorContext';
import DisplayError from '../../components/decorators/DisplayError';

import { basicAccess } from '../../utils/checkPermissions';
import { usePopupContext } from '../../contexts/PopupContext';

const AutoCompleteContainer = PrettyError(DisplayError(BaseAutoCompleteContainer));
const PREFIX = 'newBuyer.';

const AddCompanyBuyerPopupContainer = ({
  children,
  isValid,
  buyer,
  statuses,
  companyId,
  inputErrors,
  user,
  findBuyers,
  findProjects,
  findApprovalLists,
  handleUpdateCompany,
  addBuyer,
  closeValidationError,
  revertUpdate,
  updateLastResearcher,
}) => {
  const context = usePopupContext();
  const [isBasicAccess, setIsBasicAccess] = useState(false);

  useEffect(() => {
    const basicAccessFlag = basicAccess(user.getIn(['roles', 0, 'slug']));

    setIsBasicAccess(basicAccessFlag);
  }, [user]);

  const handleErrorClose = useCallback(
    (event, field) => {
      closeValidationError({ field });
    },
    [closeValidationError],
  );

  const handleClose = useCallback(() => {
    revertUpdate({ field: 'newBuyer' });
    context.closePopup();
  }, [revertUpdate, context]);

  const handleChange = useCallback(
    event => {
      const { name, value } = event.target;

      return handleUpdateCompany({ name: `newBuyer.${name}`, value });
    },
    [handleUpdateCompany],
  );

  const handleSuggestChange = useCallback(({ name, value }) => handleUpdateCompany({ name, value }), [
    handleUpdateCompany,
  ]);

  const handleCreate = useCallback(() => {
    const body = getChanged(buyer);

    body.currentStatus = body.currentStatus || '0.00';
    body.highStatus = body.highStatus || '0.00';
    addBuyer({
      companyId,
      body,
      afterSuccess: ({ dispatch }) => {
        dispatch(reloadEvents({ entityId: companyId }));
        handleClose();
        if (isBasicAccess) {
          updateLastResearcher(user.get('userName'));
        }
      },
      afterError: ({ responseBody }) => {
        context.openPopup('ErrorPopup', { formError: responseBody?.errors });
      },
    });
  }, [companyId, buyer, addBuyer, handleClose, isBasicAccess, user, context, updateLastResearcher]);

  const suggestBuyer = useMemo(
    () => (
      <AutoCompleteContainer
        highlightFirst={false}
        idName={`${PREFIX}buyerId`}
        inputProps={{ name: `${PREFIX}buyerName` }}
        name={`${PREFIX}buyerName`}
        onChange={handleSuggestChange}
        onFind={options => {
          findBuyers({
            ...options,
            field: `${PREFIX}buyerName`,
          });
        }}
        rootPath={['targetCompany', 'target']}
        suggestionsName={`${PREFIX}buyerSuggests`}
        valueName={`${PREFIX}buyerName`}
      />
    ),
    [handleSuggestChange, findBuyers],
  );

  const buyerId = unwrap(buyer.getIn(['buyerId']));

  const suggestProject = useMemo(
    () => (
      <AutoCompleteContainer
        highlightFirst={false}
        idName={`${PREFIX}projectId`}
        inputProps={{
          name: `${PREFIX}dsplProjectCategory`,
          disabled: !buyerId,
        }}
        name={`${PREFIX}dsplProjectCategory`}
        onChange={handleSuggestChange}
        onFind={options => {
          findProjects({
            ...options,
            field: `${PREFIX}dsplProjectCategory`,
            buyerIndex: 'newBuyer',
            buyerId,
          });
        }}
        render={suggestion => {
          const className = classNames({ 'text-muted': !suggestion.isActive });

          return <span className={className}>{suggestion.text}</span>;
        }}
        rootPath={['targetCompany', 'target']}
        suggestionsName={`${PREFIX}projectSuggests`}
        valueName={`${PREFIX}dsplProjectCategory`}
        suggestIfEmpty
      />
    ),
    [handleSuggestChange, findProjects, buyerId],
  );

  const projectId = unwrap(buyer.getIn(['projectId']));

  const suggestApprovalList = useMemo(
    () => (
      <AutoCompleteContainer
        highlightFirst={false}
        idName={`${PREFIX}approvalListId`}
        inputProps={{ name: `${PREFIX}dsplApplistLabel`, disabled: !projectId }}
        name={`${PREFIX}dsplApplistLabel`}
        onChange={handleSuggestChange}
        onFind={options => {
          findApprovalLists({
            ...options,
            field: `${PREFIX}dsplApplistLabel`,
            buyerIndex: 'newBuyer',
            buyerId,
            projectId,
          });
        }}
        rootPath={['targetCompany', 'target']}
        suggestionsName={`${PREFIX}approveSuggests`}
        valueName={`${PREFIX}dsplApplistLabel`}
      />
    ),
    [handleSuggestChange, findApprovalLists, buyerId, projectId],
  );

  const suggests = useMemo(
    () => ({
      suggestBuyer,
      suggestProject,
      suggestApprovalList,
    }),
    [suggestBuyer, suggestProject, suggestApprovalList],
  );

  return (
    <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={handleErrorClose}>
      <>
        <Popups.AddCompanyBuyerPopup
          buyer={buyer}
          statuses={statuses}
          {...suggests}
          isValid={isValid && buyerId && projectId}
          onChange={handleChange}
          onClose={handleClose}
          onCreate={handleCreate}
        />
        {children}
      </>
    </PrettyErrorProvider>
  );
};

function mapStateToProps(state, props) {
  const companyId = props.popup.getIn(['props', 'companyId']);

  return {
    ...props,
    companyId,
    isValid: state.targetCompany.target.get('newBuyerIsValid', false),
    inputErrors: state.targetCompany.target.get('newBuyerInputErrors', Map()),
    buyer: state.targetCompany.target.get('newBuyer'),
    statuses: fromJS(state.statuses.get('targetStatuses')),
    user: state.auth.get('user', null),
  };
}

export { AddCompanyBuyerPopupContainer };
export default connect(
  mapStateToProps,
  {
    findBuyers,
    findProjects,
    handleUpdateCompany,
    findApprovalLists,
    addBuyer,
    reloadEvents,
    closeValidationError,
    revertUpdate,
    updateLastResearcher,
  },
  mergeProps,
  connectOptions,
)(memo(AddCompanyBuyerPopupContainer));
